import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DocumentDetailsDto, DocumentNode } from '../../types/documents';
import { documentService } from '../../services/documentService';
import { useToast } from '../../components/UI/ToastProvider';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import DocumentContent from '../../components/Documents/DocumentContent';
import { useMenuPermissions } from '../../hooks/useMenuPermissions';
import { isValidRouteDocumentId } from '../../utils/documentRouteUtils';

function mapDocumentDetailsToNode(documentDetails: DocumentDetailsDto, fallbackDocumentId: string): DocumentNode {
  return {
    id: documentDetails.id || fallbackDocumentId,
    customerId: documentDetails.customerId,
    title: documentDetails.title,
    description: documentDetails.description || '',
    nodeType: documentDetails.nodeType as 'folder' | 'document' | 'file' | 'link' | 'edoc',
    parentDocumentId: documentDetails.parentDocumentId || null,
    level: 0,
    order: 0,
    children: [],
    countChild: 0,
    path: documentDetails.path,
    fullPath: documentDetails.path,
    status: documentDetails.status || 'edit',
    requiresSignature: documentDetails.requiresSignature,
    publicationExpiresAt: documentDetails.publicationExpiresAt,
    currentVersion: documentDetails.currentVersion,
    content: documentDetails.content,
    metadata: {
      fileName: documentDetails.title,
      contentType: '',
      size: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      author: '',
      lastModifiedBy: '',
      lastModifiedAt: new Date().toISOString(),
    },
  };
}

const DocumentDetailsPage: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const { getHomePage } = useMenuPermissions();
  
  const [selectedNode, setSelectedNode] = useState<DocumentNode | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState<DocumentNode | null>(null);

  useEffect(() => {
    const loadDocument = async () => {
      console.log('DocumentDetailsPage: Carregando documento com ID:', documentId);
      
      // Verificar se o usuário está autenticado
      const userToken = sessionStorage.getItem('@KnowInDoc:accessToken');
      
      if (!userToken) {
        console.log('DocumentDetailsPage: Usuário não autenticado');
        addToast('Usuário não autenticado. Faça login novamente.', 'error');
        // Redirecionar para login após 2 segundos
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        return;
      }

      if (!documentId || !isValidRouteDocumentId(documentId)) {
        console.log('DocumentDetailsPage: ID do documento inválido ou ausente');
        addToast('Link do documento inválido.', 'error');
        setIsLoading(false);
        navigate(getHomePage(), { replace: true });
        return;
      }

      try {
        setIsLoading(true);
        console.log('DocumentDetailsPage: Buscando detalhes do documento...');
        
        // Buscar detalhes do documento
        const documentDetails = await documentService.getDocumentDetails(documentId);
        console.log('DocumentDetailsPage: Documento encontrado:', documentDetails);
        
        if (!documentDetails) {
          console.log('DocumentDetailsPage: Documento não encontrado');
          addToast('Documento não encontrado', 'error');
          navigate('/documents');
          return;
        }

        // Converter DocumentDetailsDto para DocumentNode
        const documentNode = mapDocumentDetailsToNode(documentDetails, documentId);

        setSelectedNode(documentNode);
        console.log('DocumentDetailsPage: Documento carregado com sucesso');
      } catch (error: any) {
        console.error('Erro ao carregar documento:', error);
        
        if (error.response?.status === 401) {
          addToast('Sessão expirada. Redirecionando para login...', 'error');
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        } else {
          addToast('Erro ao carregar documento', 'error');
          navigate('/documents');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadDocument();
    // Recarrega apenas quando o id da rota muda (ex.: clone → novo documento).
  }, [documentId]);

  const handleBackToDocuments = () => {
    // Verificar se há informações de retorno no state da navegação
    const returnTo = location.state?.returnTo || '/documents';
    const returnToLabel = location.state?.returnToLabel || 'Documentos';
    
    navigate(returnTo);
  };

  // Obter informações de retorno para exibir no botão
  const getReturnInfo = () => {
    const returnTo = location.state?.returnTo || '/documents';
    const returnToLabel = location.state?.returnToLabel || 'Documentos';
    
    return { returnTo, returnToLabel };
  };

  const handleNodeSelect = (node: DocumentNode) => {
    navigate('/documents', {
      state: { selectFolderPath: node.path || '/' },
    });
  };

  const handleDeleteDocument = (node: DocumentNode) => {
    setNodeToDelete(node);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!nodeToDelete) return;
    try {
      await documentService.deleteNode(nodeToDelete.id);
      addToast('Excluído com sucesso!', 'success');
      setShowDeleteModal(false);
      setNodeToDelete(null);
      const returnTo = location.state?.returnTo || '/documents';
      navigate(returnTo);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      addToast(
        'Erro ao excluir: ' + (ax?.response?.data?.message || ax?.message || 'Erro desconhecido'),
        'error'
      );
    }
  };

  const handleNewDocument = () => {};
  const handleNewFolder = () => {};
  const handleOpenDocument = () => {};
  const handleDuplicateDocument = () => {};
  const handleMoveDocument = () => {};
  const handleDocumentUpdated = useCallback(
    async (updatedDocumentId: string) => {
      if (!documentId || !isValidRouteDocumentId(documentId) || updatedDocumentId !== documentId)
      {
        return;
      }

      try
      {
        const documentDetails = await documentService.getDocumentDetails(documentId);
        if (!documentDetails)
        {
          return;
        }

        setSelectedNode(mapDocumentDetailsToNode(documentDetails, documentId));
      }
      catch
      {
        addToast('Não foi possível atualizar os dados do documento.', 'error');
      }
    },
    [documentId, addToast]
  );
  const handleEditDocument = () => {};

  const handlePublishDocument = async (
    node: DocumentNode,
    options?: { publicationExpiresAt?: string | null }
  ) => {
    await documentService.publishDocument(node.id, {
      publicationExpiresAt: options?.publicationExpiresAt ?? null,
    });
    setSelectedNode(prev => prev ? { ...prev, status: 'published' } : prev);
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando documento...</p>
        </div>
      </div>
    );
  }

  if (!selectedNode) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Documento não encontrado</p>
          <button
            onClick={handleBackToDocuments}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Voltar aos Documentos
          </button>
        </div>
      </div>
    );
  }

  const { returnToLabel } = getReturnInfo();

  return (
    <div className="flex-1">
      {/* Header com botão de voltar */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={handleBackToDocuments}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Voltar para {returnToLabel}
          </button>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Detalhes do Documento</h1>
            <p className="text-gray-600">Visualize e gerencie as informações do documento.</p>
          </div>
        </div>
      </div>

      {/* Área de conteúdo do documento */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <DocumentContent
          selectedNode={selectedNode}
          onNodeSelect={handleNodeSelect}
          onNewDocument={handleNewDocument}
          onNewFolder={handleNewFolder}
          onOpenDocument={handleOpenDocument}
          onDeleteDocument={handleDeleteDocument}
          onDocumentUpdated={handleDocumentUpdated}
          onEditDocument={handleEditDocument}
          onPublishDocument={handlePublishDocument}
        />
      </div>

      {showDeleteModal && nodeToDelete && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-lg">
            <h2 className="text-lg font-semibold mb-4">Confirmar exclusão</h2>
            <p className="mb-6">{`Tem certeza que deseja excluir "${nodeToDelete.title}"?`}</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setNodeToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void confirmDelete()}
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentDetailsPage;
